import { create } from 'zustand';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged, type User as FBUser } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../lib/firebase/app';
import type { User } from '../types';

interface AuthState {
  firebaseUser: FBUser | null;
  adminUser: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  init: () => () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  firebaseUser: null,
  adminUser: null,
  loading: true,
  error: null,

  login: async (email, password) => {
    set({ loading: true, error: null });
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const snap = await getDoc(doc(db, 'users', cred.user.uid));
      if (!snap.exists()) throw new Error('사용자 정보를 찾을 수 없습니다.');
      const userData = { uid: snap.id, ...snap.data() } as User;
      if (!userData.isAdmin && !userData.isOperator) {
        await signOut(auth);
        throw new Error('관리자 또는 운영자 권한이 필요합니다.');
      }
      set({ firebaseUser: cred.user, adminUser: userData, loading: false });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '로그인 실패';
      set({ error: msg, loading: false });
      throw err;
    }
  },

  logout: async () => {
    await signOut(auth);
    set({ firebaseUser: null, adminUser: null });
  },

  init: () => {
    return onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const snap = await getDoc(doc(db, 'users', user.uid));
          if (snap.exists()) {
            const userData = { uid: snap.id, ...snap.data() } as User;
            if (userData.isAdmin || userData.isOperator) {
              set({ firebaseUser: user, adminUser: userData, loading: false });
              return;
            }
          }
        } catch { /* fall through */ }
        await signOut(auth);
      }
      set({ firebaseUser: null, adminUser: null, loading: false });
    });
  },
}));
